import { Fragment, useContext, useEffect, useState } from "react";

import { AssetLocationTypes, AssetMovement, AssetType, MovementReasonType, api } from "../../../../logic/api";

import flex from "../../../../styles/flex";
import styles from "./styles";

import { formatCamel } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";
import { SessionContext } from "../../../../utility/useSession";

import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import { useReleaseManagement, ReleaseFlag } from "../../../../utility/feature-management";
import { useTranslation, Trans } from "react-i18next";

interface MovementData {
  id: string,
  name: string,
}

const AssetHistoryTable = (props: {
  bookingId: number,
  assetType?: AssetType,
}) => {
  const session = useContext(SessionContext);
  const { toFormattedDateTimeString } = useDate();
  const { t } = useTranslation();

  const getMovements = api.hooks.booking.getMovementHistory();
  const [movements, setMovements] = useState<AssetMovement[]>([]);

  const isUnknownMovementReleased = useReleaseManagement(ReleaseFlag.releaseUnknownMovement);

  useEffect(() => {
    getMovements.send(session.current.context, {
      bookingId: props.bookingId
    });
  }, [props.bookingId]);

  useEffect(() => {
    if (getMovements.data != null) {
      setMovements(getMovements.data.filter(movement => movement.reason !== MovementReasonType.bookingCreation));
    } else {
      setMovements([]);
    }
  }, [getMovements.data]);

  const getBoxName = (movement: AssetMovement) => {
    const data = movement.additionalData as unknown as MovementData;

    return data.name;
  }

  const getMovementString = (movement: AssetMovement) => {
    const { assetType, location, isInfo } = movement;

    if (isInfo) {
      return <div>
        <b>{getBoxName(movement)}</b> {t("general.scannedTo")} <b>{location.name}</b> {t("general.by")} <b>{movement.movedBy.firstName} {movement.movedBy.lastName}</b>
      </div>
    }

    const excludeParentTypes = [AssetLocationTypes.User];
    const locationName = excludeParentTypes.includes(location.type)
      ? location.name
      : [location.parentName, location.name].filter(Boolean).join(" / ");

    if (assetType === AssetType.vehicle) {
      return isUnknownMovementReleased &&
        !movement.movedBy &&
        movement.recordedBy ? (
        <Trans
          i18nKey="booking.bookingHistory.assetMovements.vehicleLocationUpdated"
          values={{
            locationName,
            recordedByFirstName: movement.recordedBy.firstName,
            recordedByLastName: movement.recordedBy.lastName
          }}
          components={{ b: <strong /> }}
        />
      ) :
        movement.recordedBy &&
          movement.recordedBy.id !== movement.movedBy.id ? (
          <Trans
            i18nKey="booking.bookingHistory.assetMovements.vehicleMovementRecordedBy"
            values={{
              locationName,
              firstName: movement.movedBy.firstName,
              lastName: movement.movedBy.lastName,
              recordedByFirstName: movement.recordedBy.firstName,
              recordedByLastName: movement.recordedBy.lastName
            }}
            components={{ b: <strong /> }}
          />
        ) : !movement.movedBy ? (
          <Trans
            i18nKey="booking.bookingHistory.assetMovements.vehicleLocationUpdatedSimple"
            values={{ locationName }}
            components={{ b: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="booking.bookingHistory.assetMovements.vehicleMovement"
            values={{
              locationName,
              firstName: movement.movedBy.firstName,
              lastName: movement.movedBy.lastName
            }}
            components={{ b: <strong /> }}
          />
        );
    }

    if (assetType === AssetType.vehicleKey) {
      return isUnknownMovementReleased &&
        !movement.movedBy &&
        movement.recordedBy ? (
        <Trans
          i18nKey="booking.bookingHistory.assetMovements.keyLocationUpdated"
          values={{
            locationName,
            recordedByFirstName: movement.recordedBy.firstName,
            recordedByLastName: movement.recordedBy.lastName
          }}
          components={{ b: <strong /> }}
        />
      ) : movement.recordedBy &&
        movement.movedBy &&
        movement.recordedBy.id !== movement.movedBy.id ? (
        <Trans
          i18nKey="booking.bookingHistory.assetMovements.keyMovementRecordedBy"
          values={{
            locationName,
            firstName: movement.movedBy.firstName,
            lastName: movement.movedBy.lastName,
            recordedByFirstName: movement.recordedBy.firstName,
            recordedByLastName: movement.recordedBy.lastName
          }}
          components={{ b: <strong /> }}
        />
      ) : !movement.movedBy ? (
        <Trans
          i18nKey="booking.bookingHistory.assetMovements.keyLocationUpdatedSimple"
          values={{ locationName }}
          components={{ b: <strong /> }}
        />
      ) : (
        <Trans
          i18nKey="booking.bookingHistory.assetMovements.keyMovement"
          values={{
            locationName,
            firstName: movement.movedBy.firstName,
            lastName: movement.movedBy.lastName
          }}
          components={{ b: <strong /> }}
        />
      );
    }
  };

  return <ProgressView status={getMovements.status}>
    {
      movements.length == 0 ? (
        <div>{t("input.validations.noAssetHistory")}</div>
      ) : (
        <div>{movements.map((movement, i) => {
          const movementDate = toFormattedDateTimeString(new Date(movement.movementDate));

          if (props.assetType == movement.assetType || props.assetType == null) {

            return <div key={i} style={flex.flexRows}>
              <div style={flex.flexAuto}><div style={styles.cell}>{movementDate}</div></div>
              <div style={flex.flexFill}>
                <div style={styles.cell}>
                  <>
                    {movement.reason != MovementReasonType.standard && (
                      <div><i style={{ color: "gray" }}>{formatCamel(MovementReasonType[movement.reason])}</i></div>
                    )}

                    {getMovementString(movement)}
                  </>
                </div>
              </div>
            </div>
          } else {
            return <Fragment key="-1"></Fragment>
          }
        })}</div>
      )
    }

  </ProgressView>
}

export default AssetHistoryTable;
